import { animate } from "motion/mini";
import { scroll, inView } from "motion";
import {
    customEasings,
    motionEasings,
    getOffsetForScroll,
    getOffsetForScrollFunctionForStagger,
    observeForChanges,
    injectCSS,
    dispatchEvent,
} from "./Helper";

// These functions can be visualised at http://easings.net
const easingTypes = {
    ...motionEasings,
    ...customEasings,
    linear: "linear",
};

const namespace = "animation";

// Settins
const defaults: {
    duration: number;
    easing: string | ((t: number) => number);
    showTextAfter: string;
    initClass: string;
    outViewClass: string;
    inViewDelay: number;
    repeatInView: boolean;
    offsetForScroll: any;
    animation: {
        [key: string]: number | string;
    };
    selectors: {
        [key: string]: number | string | null | false;
    };
} = {
    duration: 1,
    easing: "easeOut",
    showTextAfter: "200px",
    initClass: `${namespace}-init`,
    outViewClass: `${namespace}-outview`,
    inViewDelay: 0.2,
    repeatInView: false,
    offsetForScroll: [
        [0, 1],
        [1, 1],
    ], // ScrollOffset.Enter ["start end", "end end"]
    animation: {
        filter: "none",
        transform: "none",
    },
    selectors: {
        text: 'h1,h2,h3,h4,h5,h6,p,li,table,address,blockquote,.btn[type="button"],a.btn,form',
        media: "self",
        elements: false,
    },
};

const dataAttribute = `data-${namespace}`;
let styles = "";
Object.entries(defaults.selectors).forEach(([type, selectorFromConfig]) => {
    const attributeName = `${dataAttribute}-${type}`;
    const additionalSelectorClass = `${namespace}-${type}`;
    styles += `[${attributeName}].${additionalSelectorClass}-self:not([${dataAttribute}]),[${attributeName}] .${additionalSelectorClass}:not([${dataAttribute}]){visibility:hidden}`;

    [...document.querySelectorAll(`[${attributeName}]`)].forEach((section) => {
        const data = section.getAttribute(attributeName) || "";
        if (!data) {
            return;
        }
        let trigger: string;
        let config: any = {};
        if (data === "view" || data === "scroll") {
            // no special settings
            trigger = data;
        } else {
            config = JSON.parse(data);
            trigger = config.trigger;
        }

        const triggerIsView = trigger === "view";
        const triggerIsScroll = trigger === "scroll";

        if (!triggerIsView && !triggerIsScroll) {
            return;
        }

        const getConfig = (key) => {
            const fromAttribute = section.getAttribute(`${attributeName}-${key}`);
            if (fromAttribute === "false") {
                return false;
            }
            if (fromAttribute === "true" || fromAttribute === "") {
                return true;
            }
            const fallback = fromAttribute || config[key];
            return fallback === false ? false : fallback || null;
        };

        let { easing, duration, offsetForScroll, repeatInView } = defaults;
        const animation = getConfig("animation");
        const stagger = getConfig("stagger");
        const easingString = getConfig("easing");
        const rootSelector = getConfig("root");
        const axis = getConfig("axis");

        // Set easing
        if (easingString in easingTypes) {
            easing = easingTypes[easingString];
        }

        // View has additonal settings duration and repeat
        if (triggerIsView) {
            repeatInView = !!getConfig("repeat");
            duration = parseFloat(getConfig("duration")) || duration;
        }

        // Scroll has additonal setting for offset
        if (triggerIsScroll) {
            // Each offset defines where the target will meet the container.
            // So for example, "start end" means when the start of the target meets the end of the container.
            // start is 0, center is 0.5, end is 1
            // ["start end", "end end"] is the same as [[0, 1], [1, 1]]

            let [offsetValue, offsetUnit] = type == "text" ? getOffsetForScroll(defaults.showTextAfter) : [1, null];
            const offsetConfig = getConfig("offset");

            if (offsetConfig) {
                const [value, unit] = getOffsetForScroll(offsetConfig);
                offsetValue = value;
                offsetUnit = unit;
            }

            // Stagger returns a function
            if (stagger) {
                const staggerFunction = getOffsetForScrollFunctionForStagger({
                    offsetValue,
                    offsetUnit,
                    stagger,
                    section,
                });
                if (staggerFunction) {
                    offsetForScroll = staggerFunction;
                }
            }

            if (typeof offsetForScroll !== "function") {
                if (offsetUnit) {
                    offsetForScroll = [
                        [0, 1],
                        [`${offsetValue}${offsetUnit}`, 1],
                    ];
                } else {
                    offsetForScroll = [
                        [0, 1],
                        [offsetValue, 1],
                    ];
                }
            }
        }

        const watch = getConfig("watch");
        let selector = getConfig("selector");
        if (!selector && selector !== false) {
            selector = selectorFromConfig || "";
        }
        const useSectionElement = selector === "self" || section.classList.contains(`${additionalSelectorClass}-self`);
        if (!useSectionElement) {
            const hasSelector = selector && selector !== "false";
            selector = hasSelector ? `${selector},.${additionalSelectorClass}` : `.${additionalSelectorClass}`;
        }

        const options = { useSectionElement, section, selector, animation };

        const callFunction = () => {
            let root = rootSelector ? section.querySelector(rootSelector) || section.closest(rootSelector) : null;
            root = root || undefined;
            const elements = getElementsAndAddAttribute(options);
            caller(trigger, {
                elements,
                easing,
                duration,
                stagger,
                offsetForScroll,
                repeatInView,
                root,
                axis,
                section,
            });
            if (!watch || trigger === "view") {
                return;
            }
            setTimeout(() => {
                if (root instanceof HTMLElement) {
                    root.scrollTop += 1;
                } else {
                    window.scrollBy(0, 1);
                }
            }, 100);
        };

        // Don't watch for changes
        if (watch === null) {
            callFunction();
            return;
        }

        const watchArray = Array.isArray(watch) ? watch : [watch];
        watchArray.forEach((watch) => {
            const once = watch === "once";
            if (once || watch === true) {
                observeForChanges(callFunction, section, once);
                return;
            }
            if (typeof watch !== "string") {
                console.error("Invalid watch value", watch);
                return;
            }

            let options = {};
            if (watch.endsWith("#once")) {
                // Remove #once at the end
                watch = watch.slice(0, -5);
                options = { once: true };
            }

            if (watch.endsWith("#window")) {
                // Remove #window at the end
                const eventName = watch.slice(0, -7);
                window.addEventListener(eventName, callFunction, options);
            }

            if (watch.endsWith("#document")) {
                // Remove #document at the end
                const eventName = watch.slice(0, -9);
                window.addEventListener(eventName, callFunction, options);
            }
        });
    });
});
injectCSS(styles);

function caller(trigger, options) {
    if (trigger == "scroll") {
        initOnScroll(options);
        return;
    }
    initOnView(options);
}

function initOnScroll({ elements, easing, offsetForScroll, root, axis, section }) {
    const offsetIsFuntion = typeof offsetForScroll === "function";
    const numberOfElements = elements.length;
    elements.forEach((element, index) => {
        scroll(
            animate(element, defaults.animation, {
                ease: easing,
                filter: {
                    ease: "easeOut",
                },
            }),
            {
                target: element,
                container: root,
                axis: axis || "y",
                offset: offsetIsFuntion ? offsetForScroll(index, numberOfElements) : offsetForScroll,
            },
        );
    });
    // Fire event
    dispatchEvent(section, "init", { trigger: "scroll", elements });
}

function initOnView({ elements, easing, duration, stagger, repeatInView, root, section }) {
    const numberOfElements = elements.length;
    elements.forEach((element) => element.classList.add(defaults.outViewClass));
    let delay = defaults.inViewDelay;
    inView(
        elements,
        (target) => {
            target.classList.remove(defaults.outViewClass);
            if (stagger) {
                const index = elements.findIndex((item) => item == target);
                if (stagger === true) {
                    const initDelay = defaults.inViewDelay;
                    const step = duration / (numberOfElements - 1 || 1);
                    delay = Math.round((index * step + initDelay) * 100) / 100;
                } else {
                    delay += index * parseFloat(stagger);
                }
            }

            const controls = animate(target, defaults.animation, {
                delay,
                duration,
                ease: easing,
                filter: {
                    ease: "easeOut",
                },
            });
            if (repeatInView) {
                return ({ target }) => {
                    controls.cancel();
                    Object.keys(defaults.animation).forEach((key) => {
                        (target as HTMLElement).style[key] = "";
                    });
                };
            }
        },
        {
            amount: 0.3,
            root,
        },
    );
    // Fire event
    dispatchEvent(section, "init", { trigger: "view", elements });
}

function getElementsAndAddAttribute({ useSectionElement, section, selector, animation }) {
    if (useSectionElement && section.classList.contains(defaults.initClass)) {
        return [];
    }

    const elements = useSectionElement
        ? [section]
        : [...section.querySelectorAll(`:is(${selector}):not(.${defaults.initClass})`)].filter((element) => {
              // Filter out elements who have another animation settings
              const elementHasAnimation = Object.keys(defaults.selectors).some((key) => {
                  if (element.hasAttribute(`${dataAttribute}-${key}`)) {
                      return true;
                  }
                  return false;
              });
              return !elementHasAnimation;
          });

    elements.forEach((element) => {
        if (!element.hasAttribute(dataAttribute)) {
            element.setAttribute(dataAttribute, animation || "");
        }
    });

    return elements;
}
